/**
 * Time type system for TradesMate Pro
 *
 * SCOPE: time tracking here is for BILLING + scheduling blocks ONLY — this is NOT a payroll app.
 * The old "industry-standard" taxonomy (admin/training/meeting/on_call/break/pto) was payroll/
 * timekeeping and has been pruned. Only billable types remain. PTO/leave is a SEPARATE feature
 * (the PTO tab writes to `employee_time_off`, not `employee_timesheets`) — do NOT re-add `pto`
 * (or the other overhead types) here; the DB CHECK on `employee_timesheets.time_type` is aligned
 * to this set.
 */

export const TIME_TYPES = {
  JOB: 'job',
  TRAVEL: 'travel'
};

/**
 * Time type configuration
 * Defines display name, icon, color, job requirement, and billability
 */
export const TIME_TYPE_CONFIG = {
  [TIME_TYPES.JOB]: {
    label: 'Job Work',
    description: 'Billable work on a specific job',
    jobRequired: true,
    billable: true,
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    icon: 'BriefcaseIcon',
    order: 1
  },
  [TIME_TYPES.TRAVEL]: {
    label: 'Travel',
    description: 'Travel time to/from job site',
    jobRequired: false,
    billable: true,
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    icon: 'MapPinIcon',
    order: 2
  }
};

/**
 * Get time type configuration
 * @param {string} timeType - The time type key
 * @returns {object} Configuration object for the time type
 */
export function getTimeTypeConfig(timeType) {
  return TIME_TYPE_CONFIG[timeType] || TIME_TYPE_CONFIG[TIME_TYPES.JOB];
}

/**
 * Check if a time type requires a job
 * @param {string} timeType - The time type key
 * @returns {boolean} True if job is required
 */
export function isJobRequired(timeType) {
  const config = getTimeTypeConfig(timeType);
  return config.jobRequired;
}

/**
 * Get all time types sorted by order
 * @returns {array} Array of time type keys sorted by display order
 */
export function getTimeTypesSorted() {
  return Object.keys(TIME_TYPE_CONFIG)
    .sort((a, b) => TIME_TYPE_CONFIG[a].order - TIME_TYPE_CONFIG[b].order);
}

/**
 * Get billable time types
 * @returns {array} Array of billable time type keys
 */
export function getBillableTimeTypes() {
  return Object.keys(TIME_TYPE_CONFIG)
    .filter(type => TIME_TYPE_CONFIG[type].billable);
}
